import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingCart, Star, Flame, Smartphone, Headphones, Refrigerator, Sun, BatteryFull, Tv, Watch, Camera, Laptop, Gamepad2 } from "lucide-react";
import { api, productImageUrl, BACKEND_URL } from "../lib/api";
import ProductCard from "../components/ProductCard";
import QuickViewModal from "../components/QuickViewModal";
import VideoEmbed from "../components/VideoEmbed";
import useCountdown from "../hooks/useCountdown";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";

const HERO_SLIDES = [
  { badge: "🆕 Nouveau arrivage", title: "iPhone 15 Pro Max", subtitle: "Puissance ultime. Système photo Pro 48 Mpx.", cta: "Acheter maintenant", link: "/products?category=telephones", img: "https://images.pexels.com/photos/32912307/pexels-photo-32912307.jpeg?auto=compress&cs=tinysrgb&h=650&w=940" },
  { badge: "⚡ Livraison Immédiate", title: "Batteries Lithium", subtitle: "6000 cycles. Autonomie premium.", cta: "Voir la gamme", link: "/products?category=batteries", img: "https://images.pexels.com/photos/33751679/pexels-photo-33751679.jpeg?auto=compress&cs=tinysrgb&h=650&w=940" },
  { badge: "☀ Énergie solaire", title: "Panneaux 450W", subtitle: "Autonomie complète pour votre maison.", cta: "Découvrir", link: "/products?category=energie-solaire", img: "https://images.pexels.com/photos/9799720/pexels-photo-9799720.jpeg?auto=compress&cs=tinysrgb&h=650&w=940" },
];

const CIRCLE_CATS = [
  { slug: "telephones", name: "Téléphones", Icon: Smartphone },
  { slug: "audio", name: "Audio", Icon: Headphones },
  { slug: "electromenager", name: "Électroménager", Icon: Refrigerator },
  { slug: "energie-solaire", name: "Solaire", Icon: Sun },
  { slug: "batteries", name: "Batteries", Icon: BatteryFull },
  { slug: "telephones", name: "Ordinateurs", Icon: Laptop },
  { slug: "audio", name: "TV & Audio", Icon: Tv },
  { slug: "telephones", name: "Montres", Icon: Watch },
  { slug: "telephones", name: "Caméras", Icon: Camera },
];

const HOT_DEALS_TARGET = Date.now() + 3 * 24 * 60 * 60 * 1000;

const CountBox = ({ label, value }) => (
  <div className="flex flex-col items-center">
    <div className="bg-white text-[#0052CC] rounded-md min-w-[44px] py-1.5 text-center font-extrabold text-lg shadow" style={{fontFamily:"Outfit"}}>{String(value).padStart(2,"0")}</div>
    <span className="text-[9px] uppercase tracking-widest text-white/80 mt-1 font-semibold">{label}</span>
  </div>
);

const TABS = [
  { key: "all", label: "Tous" },
  { key: "telephones", label: "Smartphones" },
  { key: "audio", label: "Audio" },
  { key: "electromenager", label: "Électroménager" },
  { key: "energie-solaire", label: "Solaire" },
  { key: "batteries", label: "Batteries" },
];

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-3 text-left">
    <span className="w-1.5 h-7 bg-[#E3000F] rounded-sm" />
    <h2 className="text-2xl md:text-3xl font-extrabold uppercase tracking-tight" style={{fontFamily:"Outfit"}}>{children}</h2>
  </div>
);

// Small horizontal product card (Hot Deals)
const HotDealCard = ({ product, onQuickView }) => {
  const { addItem } = useCart();
  const { format: formatGdes } = useCurrency();
  const disc = product.is_promo && product.discount_percent > 0 ? Math.round(product.price * (100 - product.discount_percent))/100 : null;
  return (
    <Link to={`/products/${product.product_id}`} className="je-card group flex flex-col" data-testid={`hot-${product.product_id}`}>
      <div className="relative w-full overflow-hidden" style={{ height: "170px" }}>
        <img src={productImageUrl(product.images?.[0])} alt={product.name} loading="lazy" className="block w-full h-full object-cover group-hover:scale-110 transition duration-500" />
        {disc && <span className="absolute top-2 left-2 bg-[#E3000F] text-white text-[10px] font-extrabold px-2 py-0.5 rounded">−{product.discount_percent}%</span>}
      </div>
      <div className="p-3 flex flex-col flex-1">
        <p className="text-xs font-medium text-gray-700 line-clamp-2 group-hover:text-[#0052CC]" style={{minHeight:"2em"}}>{product.name}</p>
        <div className="flex items-center gap-0.5 mt-1">
          {Array.from({length:5}).map((_,i) => <Star key={i} className={`w-3 h-3 ${i < Math.round(product.rating) ? "je-star fill-current" : "text-gray-300"}`} />)}
          <span className="text-[10px] text-gray-500 ml-1">({product.reviews_count})</span>
        </div>
        <div className="flex items-baseline gap-1.5 mt-1 flex-wrap">
          {disc ? (
            <>
              <span className="font-extrabold text-[#0052CC] text-sm" style={{fontFamily:"Outfit"}}>{formatGdes(disc)}</span>
              <span className="text-[10px] text-gray-400 line-through">{formatGdes(product.price)}</span>
            </>
          ) : (
            <span className="font-extrabold text-[#0052CC] text-sm" style={{fontFamily:"Outfit"}}>{formatGdes(product.price)}</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-green-700 font-semibold">✓ En stock</span>
          <button onClick={(e) => { e.preventDefault(); if (product.stock <= 0) { toast.error("Rupture"); return; } addItem(product, 1); toast.success("Ajouté au panier"); }} className="bg-[#0052CC] hover:bg-[#003D99] text-white p-2 rounded-md transition" aria-label="Ajouter">
            <ShoppingCart className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </Link>
  );
};

export default function HomePage() {
  const { t } = useI18n();
  const { format: formatGdes } = useCurrency();
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [topSelling, setTopSelling] = useState([]);
  const [tab, setTab] = useState("all");
  const [banners, setBanners] = useState([]);
  const [footerBanners, setFooterBanners] = useState([]);
  const [videos, setVideos] = useState([]);
  const [qvProduct, setQvProduct] = useState(null);
  const cd = useCountdown(HOT_DEALS_TARGET);

  useEffect(() => {
    api.get("/banners", { params: { location: "hero" } }).then(({ data }) => setBanners(data || [])).catch(() => setBanners([]));
    api.get("/banners", { params: { location: "footer" } }).then(({ data }) => setFooterBanners(data || [])).catch(() => setFooterBanners([]));
    api.get("/videos").then(({ data }) => setVideos(data || [])).catch(() => setVideos([]));
  }, []);

  // Two default hero image banners if admin has not configured any
  const HERO_BANNERS = (banners.length > 0
    ? banners.slice(0, 2)
    : [
        {
          banner_id: "default-1",
          image_url: "https://images.pexels.com/photos/32912307/pexels-photo-32912307.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
          link: "/products?category=telephones",
        },
        {
          banner_id: "default-2",
          image_url: "https://images.pexels.com/photos/9799720/pexels-photo-9799720.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
          link: "/products?category=energie-solaire",
        },
      ]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/products", { params: { limit: 100 } });
      setProducts(data);
      setFeatured(data.filter(p => p.is_new).slice(0, 6));
      setTopSelling(data.filter(p => p.is_bestseller).slice(0, 10));
    })();
  }, []);

  const hotDeals = products.filter(p => p.is_promo && p.discount_percent > 0).slice(0, 5);
  const topFiltered = tab === "all" ? topSelling : topSelling.filter(p => p.category === tab);

  return (
    <div className="bg-[#F5F7FA]" data-testid="home-page">

      {/* HERO — 2 clickable image banners, admin-managed (no text overlay) */}
      <section className="je-container pt-4 md:pt-6" data-testid="hero-banners">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {HERO_BANNERS.map((b) => (
            <Link
              key={b.banner_id}
              to={b.link || "/products"}
              className="relative rounded-xl md:rounded-2xl overflow-hidden bg-gray-100 shadow-md hover:shadow-2xl transition group aspect-[16/9] md:aspect-[21/9]"
              data-testid={`hero-banner-${b.banner_id}`}
              aria-label="Bannière"
            >
              {b.image_url && (
                <img
                  src={b.image_url.startsWith("http") ? b.image_url : `${BACKEND_URL}${b.image_url}`}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700"
                />
              )}
            </Link>
          ))}
        </div>
      </section>

      {/* CATEGORIES row in white container */}
      <section className="je-container mt-12 md:mt-14 pt-2">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 md:p-6">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3">
            {CIRCLE_CATS.map((c, i) => (
              <Link key={i} to={`/products?category=${c.slug}`} className="group flex flex-col items-center py-2" data-testid={`home-cat-${i}`}>
                <div className="w-16 h-16 md:w-[70px] md:h-[70px] rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center group-hover:bg-[#0052CC]/10 group-hover:border-[#0052CC] transition-all">
                  <c.Icon className="w-7 h-7 md:w-8 md:h-8 text-[#0052CC] group-hover:scale-110 transition" />
                </div>
                <p className="mt-2 text-xs font-semibold text-gray-800 text-center">{c.name}</p>
                <p className="text-[10px] text-gray-500">{Math.floor(Math.random()*80)+10} {t("productsCount")}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOT DEALS */}
      <section className="je-container mt-16 md:mt-20 pt-2">
        <div className="bg-gradient-to-r from-[#0052CC] via-[#0052CC] to-[#003D99] rounded-2xl overflow-hidden shadow-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4 text-white flex-wrap gap-3">
            <div className="inline-flex items-center gap-2">
              <Flame className="w-6 h-6 text-orange-400" />
              <h2 className="text-xl md:text-2xl font-extrabold uppercase tracking-wide" style={{fontFamily:"Outfit"}}>{t("hotDeals")}</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-widest font-bold text-white/80">{t("offerEndsIn")}</span>
              <div className="flex gap-1.5">
                <CountBox label={t("days")} value={cd.days} />
                <CountBox label={t("hours")} value={cd.hours} />
                <CountBox label={t("minutes")} value={cd.minutes} />
                <CountBox label={t("seconds")} value={cd.seconds} />
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-[240px_1fr] lg:grid-cols-[280px_1fr] gap-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 flex flex-col justify-between text-white">
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-white/80">{t("saveUpTo")}</p>
                <p className="text-6xl font-extrabold mt-2" style={{fontFamily:"Outfit"}}>80%</p>
                <p className="mt-3 text-sm text-white/85">{t("limitedOffer")}</p>
              </div>
              <Link to="/products?category=promotions" className="mt-4 bg-white text-[#0052CC] font-bold text-sm px-4 py-2.5 rounded text-center inline-block hover:bg-white/90 transition">{t("discoverOffers")}</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {hotDeals.length > 0 ? hotDeals.slice(0, 4).map((p) => <HotDealCard key={p.product_id} product={p} onQuickView={setQvProduct} />)
                : products.slice(0, 4).map((p) => <HotDealCard key={p.product_id} product={p} onQuickView={setQvProduct} />)}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="je-container mt-16 md:mt-20 pt-2">
        <div className="flex items-center justify-between mb-6">
          <SectionTitle>Featured Products</SectionTitle>
          <Link to="/products" className="text-[#0052CC] hover:underline text-sm font-semibold inline-flex items-center gap-1" data-testid="featured-view-all">Voir tout <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-testid="featured-grid">
          {featured.map((p) => <ProductCard key={p.product_id} product={p} onQuickView={setQvProduct} />)}
        </div>
      </section>

      {/* TOP SELLING */}
      <section className="je-container mt-16 md:mt-20 pt-2 mb-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <SectionTitle>{t("topSelling")}</SectionTitle>
          <Link to="/products?category=meilleures-ventes" className="text-[#0052CC] hover:underline text-sm font-semibold inline-flex items-center gap-1">{t("viewAll")} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 md:p-6">
          <div className="flex gap-2 overflow-x-auto pb-3 mb-4 border-b border-gray-100">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold transition ${tab===t.key ? "bg-[#0052CC] text-white shadow" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`} data-testid={`top-tab-${t.key}`}>{t.label}</button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="top-selling-grid">
            {topFiltered.slice(0, 6).map((p, idx) => {
              const disc = p.is_promo && p.discount_percent > 0 ? Math.round(p.price * (100 - p.discount_percent))/100 : null;
              return (
                <Link key={p.product_id} to={`/products/${p.product_id}`} className="bg-gray-50 hover:bg-white border border-gray-100 hover:border-[#0052CC] hover:shadow-md rounded-xl overflow-hidden flex transition-all">
                  <div className="relative shrink-0 flex items-center justify-center bg-[#0052CC] text-white w-8 md:w-10 shrink-0">
                    <span className="text-2xl font-extrabold" style={{fontFamily:"Outfit"}}>{idx+1}</span>
                  </div>
                  <div style={{ width: "35%", minHeight: "140px" }} className="relative overflow-hidden bg-gray-100 shrink-0">
                    <img src={productImageUrl(p.images?.[0])} alt={p.name} loading="lazy" className="block absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                    <div>
                      <h3 className="font-semibold text-sm text-gray-900 line-clamp-2">{p.name}</h3>
                      <p className="text-[11px] text-gray-500 mt-1 capitalize">{p.category.replace("-", " ")}</p>
                      <div className="flex items-center gap-0.5 mt-1">
                        {Array.from({length:5}).map((_,i) => <Star key={i} className={`w-3 h-3 ${i < Math.round(p.rating) ? "je-star fill-current" : "text-gray-300"}`} />)}
                        <span className="text-[10px] text-gray-500 ml-0.5">({p.reviews_count})</span>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                      {disc ? (
                        <>
                          <span className="text-sm font-extrabold text-[#0052CC]" style={{fontFamily:"Outfit"}}>{formatGdes(disc)}</span>
                          <span className="text-[10px] text-gray-400 line-through">{formatGdes(p.price)}</span>
                        </>
                      ) : (
                        <span className="text-sm font-extrabold text-gray-900" style={{fontFamily:"Outfit"}}>{formatGdes(p.price)}</span>
                      )}
                      <span className="ml-auto text-[10px] font-semibold text-green-700">✓ En stock</span>
                    </div>
                  </div>
                </Link>
              );
            })}
            {topFiltered.length === 0 && <p className="col-span-full text-center text-sm text-gray-500 py-8">Aucun produit dans cette catégorie.</p>}
          </div>
        </div>
      </section>

      {/* PROMOTIONAL VIDEOS (admin-configurable) */}
      {videos.length > 0 && (
        <section className="je-container mt-16 md:mt-20 pt-2 mb-8" data-testid="promo-videos">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <SectionTitle>Vidéos</SectionTitle>
          </div>
          <div className={`grid gap-4 md:gap-5 ${videos.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
            {videos.map((v) => (
              <div key={v.video_id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <VideoEmbed video={v} />
                {(v.title || v.description) && (
                  <div className="p-4">
                    {v.title && <h3 className="font-bold text-gray-900" style={{ fontFamily: "Outfit" }}>{v.title}</h3>}
                    {v.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{v.description}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FOOTER BANNERS (admin-configurable, image-only, fully clickable) */}
      {footerBanners.length > 0 && (
        <section className="je-container mt-16 md:mt-20 pt-2 mb-12" data-testid="footer-banners">
          <div className={`grid gap-4 md:gap-5 ${footerBanners.length === 1 ? "grid-cols-1" : footerBanners.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
            {footerBanners.map((b) => (
              <Link
                key={b.banner_id}
                to={b.link || "/products"}
                className="relative rounded-2xl overflow-hidden group aspect-[16/9] block bg-gray-100 shadow-md hover:shadow-2xl transition"
                data-testid={`footer-banner-${b.banner_id}`}
                aria-label="Bannière promotionnelle"
              >
                {b.image_url && (
                  <img
                    src={b.image_url.startsWith("http") ? b.image_url : `${BACKEND_URL}${b.image_url}`}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      <QuickViewModal product={qvProduct} onClose={() => setQvProduct(null)} />
    </div>
  );
}
